import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import type { Send } from '../../types/send';
import { ALL_STYLES, countByStyle, CHART_TOOLTIP_STYLE } from '../../utils/stats-utils';

interface ClimbStyleRadarChartProps {
  sends: Send[];
}

export default function ClimbStyleRadarChart({ sends }: ClimbStyleRadarChartProps) {
  const { t } = useTranslation();

  const data = useMemo(() => {
    const counts = countByStyle(sends);
    return ALL_STYLES.map(style => ({
      label: t(`climbTypes.${style}`),
      count: counts[style],
    }));
  }, [sends, t]);

  const hasAnyStyle = data.some(d => d.count > 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('stats.climbStyleRadar.title')}</CardTitle>
      </CardHeader>
      {!hasAnyStyle ? (
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {t('stats.insufficientData')}
        </CardContent>
      ) : (
        <CardContent className="flex justify-center pb-4">
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={data} margin={{ top: 8, right: 24, left: 24, bottom: 8 }}>
              <PolarGrid />
              <PolarAngleAxis dataKey="label" tick={{ fontSize: 11 }} />
              <PolarRadiusAxis tick={{ fontSize: 10 }} />
              <Tooltip {...CHART_TOOLTIP_STYLE} />
              <Radar
                name={t('stats.climbStyleRadar.sends')}
                dataKey="count"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.4}
              />
            </RadarChart>
          </ResponsiveContainer>
        </CardContent>
      )}
    </Card>
  );
}
